import { useEffect, useRef, useContext } from 'react';
import Marzipano from 'marzipano';
import { createUseStyles } from 'react-jss';
import {
    primaryInteractives,
    rootUrl,
    secondaryInteractives,
} from '../config/config';
import HotspotNew from './hotspot/HotspotNew';
import { useState } from 'react';
import { State } from '../contexts/GlobalState';
import PanoramaSoundscape from '../assets/audio/PanoramaSoundscape.mp3';
import Lottie from './common/Lottie';
import Loader from '../assets/lotties/Loader.json';
import HandSwipe from '../assets/lotties/HandSwipe.json';
import { ReactComponent as SwipeGraphic } from '../assets/icons/SwipeGraphic.svg';
import variables from '../theme/variables';

const useStyles = createUseStyles(
    {
        wrap: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
        },

        container: {
            background: 'black',
            overflow: 'hidden',
            position: 'fixed',
            height: 'var(--app-height)',
            width: '100vw',
            top: 0,
            left: 0,
        },

        loader: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: variables.palette.grey.dark,

            transition: 'opacity 0.5s ease-out, visibility 0.5s step-end',
        },

        instruction: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: '33vh',
            background: 'rgba(0, 0, 0, 0.5)',

            transition: 'opacity 0.5s ease-out, visibility 0.5s step-end',
            pointerEvents: 'none',

            color: variables.palette.white,
            textAlign: 'center',
        },

        instructionSubtitle: {
            fontSize: 16,
            lineHeight: '22px',
            marginTop: 30,

            '@media (min-width: 1024px)': {
                fontSize: 20,
                lineHeight: '28px',
            },
        },

        instructionAnimation: {
            width: 120,
            height: 60,
            margin: '48px auto 10px',
        },
    },
    {
        name: 'Panorama',
    }
);

const Panorama = ({ onHotspotClick, onPrimaryClick }) => {
    const classes = useStyles();
    const { muted, soundscapeMuted } = useContext(State);
    const sceneRef = useRef();
    const [hotspotStates, setHotspotStates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [instruction, setInstruction] = useState(true);

    const audioRef = useRef();

    useEffect(() => {
        // Create viewer.
        var viewer = new Marzipano.Viewer(document.getElementById('marzipano'));

        const stage = viewer.stage();

        function onRenderComplete() {
            // stage.removeEventListener('renderComplete', onRenderComplete);

            setLoading(false);

            // only add this now to ensure it's not fired before screen is loaded
            function onViewMove() {
                view.removeEventListener('change', onViewMove);
                setInstruction(false);
            }

            view.addEventListener('change', onViewMove);
        }

        // stage.addEventListener('renderComplete', onRenderComplete);

        // Create source.
        var source = Marzipano.ImageUrlSource.fromString(
            `${window.rootUrl}/img/roomPanorama.webp`
        );

        // Create geometry.
        var geometry = new Marzipano.EquirectGeometry([{ width: 4000 }]);

        // Create view.
        var limiter = Marzipano.RectilinearView.limit.traditional(
            1024,
            (100 * Math.PI) / 180
        );
        var view = new Marzipano.RectilinearView({ yaw: Math.PI }, limiter);

        // Create scene.
        var scene = viewer.createScene({
            source: source,
            geometry: geometry,
            view: view,
            pinFirstLevel: true,
        });

        // this checks if the scene (ie. panorama image) is mounted
        var layer = scene.layer();
        var textureStore = layer.textureStore();
        textureStore.addEventListener('textureLoad', () => {
            var tileList = [];
            layer.visibleTiles(tileList);
            for (var i = 0; i < tileList.length; i++) {
                if (!textureStore.query(tileList[i]).hasTexture) {
                    console.log('texturerLoad - not finished');
                } else {
                    onRenderComplete();
                }
            }
        });

        // Display scene.
        scene.switchTo();

        sceneRef.current = scene;

        // yaw - left right - 0 2*PI
        // pitch - top down - -PI/2  ->  PI/2

        const hotspotOpts = {
            perspective: {
                radius: 800,
            },
        };

        // Adding hotspots to scene
        secondaryInteractives.forEach((hotspot, index) => {
            var element = document.getElementById(hotspot.id);
            var position = hotspot.position;
            scene
                .hotspotContainer()
                .createHotspot(element, position, hotspotOpts);
        });

        primaryInteractives.forEach((hotspot, index) => {
            var element = document.getElementById(hotspot.id);
            var position = hotspot.position;
            scene
                .hotspotContainer()
                .createHotspot(element, position, hotspotOpts);
        });

        // calculate hotspots distance from screen centre
        // any time the view moves or changes
        view.addEventListener('change', () => {
            const newHotspotStates = {};
            const midline = window.innerWidth / 2;

            secondaryInteractives.forEach((hotspot) => {
                const hotspotScreenCoords = view.coordinatesToScreen(
                    hotspot.position
                );
                const distanceFromCenter = hotspotScreenCoords
                    ? Math.abs(midline - hotspotScreenCoords.x)
                    : 100000; // unlikely big number - ie - out of the viewport

                newHotspotStates[hotspot.id] = distanceFromCenter;
            });

            primaryInteractives.forEach((hotspot) => {
                const hotspotScreenCoords = view.coordinatesToScreen(
                    hotspot.position
                );
                const distanceFromCenter = hotspotScreenCoords
                    ? Math.abs(midline - hotspotScreenCoords.x)
                    : 100000;

                newHotspotStates[hotspot.id] = distanceFromCenter;
            });

            setHotspotStates(newHotspotStates);
        });
    }, []);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = 0.2;
        }
    }, []);

    const handleHotspotClicked = (hotspot) => {
        onHotspotClick(hotspot);
    };

    const handlePrimaryClicked = (hotspot) => {
        onPrimaryClick(hotspot);
    };

    return (
        <div className={classes.wrap}>
            <div className={classes.container} id="marzipano"></div>

            <div
                className={classes.instruction}
                style={{
                    visibility: instruction ? 'visible' : 'hidden',
                    opacity: instruction ? 1 : 0,
                }}
            >
                <SwipeGraphic />
                <h3 className={classes.instructionSubtitle}>
                    Swipe to explore
                </h3>
            </div>

            <div
                className={classes.loader}
                style={{
                    visibility: loading ? 'visible' : 'hidden',
                    opacity: loading ? 1 : 0,
                }}
            >
                <Lottie animationData={Loader} width="100%" height="100%" />
            </div>

            {secondaryInteractives.map((hotspot, index) => (
                <HotspotNew
                    key={hotspot.id}
                    hotspot={hotspot}
                    distanceFromCenter={hotspotStates[hotspot.id]}
                    onClick={handleHotspotClicked}
                />
            ))}

            {primaryInteractives.map((hotspot, index) => (
                <HotspotNew
                    type="primary"
                    key={hotspot.id}
                    hotspot={hotspot}
                    distanceFromCenter={hotspotStates[hotspot.id]}
                    onClick={handlePrimaryClicked}
                />
            ))}

            <audio
                autoPlay
                src={PanoramaSoundscape}
                muted={muted || soundscapeMuted}
                loop
                ref={audioRef}
            />
        </div>
    );
};

export default Panorama;
